import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BriefcaseBusiness,
  Building2,
  FileUser,
  MessagesSquare,
  UserRoundSearch,
  UsersRound,
} from "lucide-react";
import { adminApi } from "@/api/admin.api";
import { AdminEntityActions } from "./AdminEntityActions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type ResourceType =
  | "jobs"
  | "companies"
  | "applications"
  | "interviews"
  | "resumes"
  | "usage"
  | "activity";

const RESOURCE_TABS: Array<{ value: ResourceType; label: string }> = [
  { value: "jobs", label: "Jobs" },
  { value: "companies", label: "Companies" },
  { value: "applications", label: "Applications" },
  { value: "interviews", label: "Interviews" },
  { value: "resumes", label: "Resumes" },
  { value: "usage", label: "Usage" },
  { value: "activity", label: "Activity" },
];

const STATUS_OPTIONS: Partial<Record<ResourceType, Array<{ value: string; label: string }>>> = {
  jobs: ["OPEN", "CLOSED", "HOLD", "IN_PROGRESS", "ARCHIVED"].map((value) => ({
    value,
    label: value,
  })),
  companies: [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "verified", label: "Verified" },
    { value: "flagged", label: "Flagged" },
  ],
  applications: [
    "APPLIED",
    "SHORTLISTED",
    "INTERVIEW_SCHEDULED",
    "INTERVIEW_DONE",
    "ON_HOLD",
    "OFFERED",
    "REJECTED",
  ].map((value) => ({ value, label: value })),
  interviews: ["PENDING", "SCHEDULED", "COMPLETED", "IN_PROGRESS", "CANCELLED"].map((value) => ({
    value,
    label: value,
  })),
  resumes: ["PENDING", "PROCESSING", "COMPLETED", "ERROR"].map((value) => ({
    value,
    label: value,
  })),
  usage: [
    { value: "success", label: "Successful" },
    { value: "failed", label: "Failed" },
  ],
  activity: [
    { value: "read", label: "Read notifications" },
    { value: "unread", label: "Unread notifications" },
    { value: "PENDING", label: "Pending invitations / calls" },
    { value: "COMPLETED", label: "Completed calls" },
  ],
};

export function RecruiterDetails() {
  const { id } = useParams();
  const [activeResource, setActiveResource] = useState<ResourceType>("jobs");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const overviewQuery = useQuery({
    queryKey: ["admin-recruiter-overview", id],
    queryFn: () => adminApi.getRecruiterOverview(id!),
    enabled: Boolean(id),
  });

  const resourceQuery = useQuery({
    queryKey: ["admin-recruiter-resource", id, activeResource, page, status, dateFrom, dateTo],
    queryFn: () =>
      adminApi.getRecruiterResource(id!, activeResource, {
        page,
        limit: 20,
        status: status || undefined,
        dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined,
      }),
    enabled: Boolean(id),
  });

  useEffect(() => {
    setPage(1);
    setStatus("");
  }, [activeResource]);

  const overview = overviewQuery.data;
  const resource = resourceQuery.data;
  const selected = overview?.selectedRecruiter;
  const stats = useMemo(
    () => [
      { label: "Memberships", value: overview?.counts?.memberships ?? 0, icon: UsersRound },
      { label: "Organizations", value: overview?.counts?.organizations ?? 0, icon: Building2 },
      { label: "Jobs", value: overview?.counts?.jobs ?? 0, icon: BriefcaseBusiness },
      { label: "Companies", value: overview?.counts?.companies ?? 0, icon: Building2 },
      { label: "Applications", value: overview?.counts?.applications ?? 0, icon: UserRoundSearch },
      { label: "Interviews", value: overview?.counts?.interviews ?? 0, icon: MessagesSquare },
      { label: "Resumes", value: overview?.counts?.resumes ?? 0, icon: FileUser },
      { label: "Usage events", value: overview?.counts?.usageEvents ?? 0, icon: Activity },
    ],
    [overview]
  );

  if (overviewQuery.isLoading)
    return <PageMessage message="Loading recruiter investigation data..." />;
  if (overviewQuery.isError)
    return <PageMessage message="Unable to load recruiter investigation data." error />;
  if (!selected) return <PageMessage message="Recruiter not found." error />;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-950">
              {selected.name || selected.email}
            </h1>
            <Badge variant={selected.emailVerified ? "success" : "warning"}>
              {selected.emailVerified ? "Verified" : "Unverified"}
            </Badge>
            <Badge variant="outline">Investigation</Badge>
          </div>
          <p className="text-sm text-slate-600">{selected.email}</p>
          <p className="mt-1 font-mono text-xs text-slate-500">
            Auth0 subject: {overview.identity.authSubId}
          </p>
        </div>
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Investigation data is read-only. Administrative actions are separated below.
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {label}
                </p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{value}</p>
              </div>
              <Icon className="h-5 w-5 text-slate-400" />
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Identity and memberships</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Context</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Profile</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Record ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.memberships.map((membership: any) => (
                  <TableRow key={membership.id}>
                    <TableCell>
                      <div className="font-medium text-slate-900">
                        {membership.authOrgId === "individual"
                          ? "Individual"
                          : membership.organization?.name || membership.company}
                      </div>
                      <div className="mt-1 font-mono text-xs text-slate-500">
                        {membership.authOrgId}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{membership.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <div>{membership.persona || "Not set"}</div>
                      <div className="text-xs text-slate-500">
                        {membership.onboardingCompleted
                          ? "Onboarding complete"
                          : "Onboarding incomplete"}
                      </div>
                    </TableCell>
                    <TableCell>{formatDateTime(membership.lastLoginAt)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {membership.id}
                      {membership.id === overview.identity.selectedRecruiterId && (
                        <Badge className="ml-2" variant="secondary">
                          Opened
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profile status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Detail label="Created" value={formatDateTime(selected.createdAt)} />
            <Detail label="Updated" value={formatDateTime(selected.updatedAt)} />
            <Detail label="Last login" value={formatDateTime(selected.lastLoginAt)} />
            <Detail label="Persona" value={selected.persona || "Not set"} />
            <Detail label="Public slug" value={selected.slug || "Not configured"} mono />
            <Detail
              label="Stripe customer"
              value={selected.stripeCustomerId || "Not linked"}
              mono
            />
          </CardContent>
        </Card>
      </section>

      <OrganizationsCard organizations={overview.organizations} />
      <BillingCard billing={overview.billing} />
      <ConfigurationCard
        configuration={overview.configuration}
        usageCounters={overview.usageCounters}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Related records</CardTitle>
              <p className="mt-1 text-sm text-slate-500">
                Candidate contact details are masked. Secrets and raw file locations are excluded.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
                className="h-9 rounded-sm border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="">All statuses</option>
                {(STATUS_OPTIONS[activeResource] || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="h-9 rounded-sm border border-slate-300 px-2 text-sm"
                aria-label="From date"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="h-9 rounded-sm border border-slate-300 px-2 text-sm"
                aria-label="To date"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TabsRoot
            value={activeResource}
            onValueChange={(value) => setActiveResource(value as ResourceType)}
          >
            <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
              {RESOURCE_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {RESOURCE_TABS.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                <ResourceTable
                  resource={tab.value}
                  data={resource}
                  loading={resourceQuery.isFetching}
                />
              </TabsContent>
            ))}
          </TabsRoot>
          <Pagination
            page={resource?.page || page}
            totalPages={resource?.totalPages || 1}
            total={resource?.total || 0}
            onPage={setPage}
          />
        </CardContent>
      </Card>

      <AdminEntityActions
        entityType="recruiter"
        entityId={selected.id}
        product="THERECRUIT"
        subscriptions={overview.billing.subscriptions}
        stripe={overview.billing.stripe}
        organizations={overview.organizations.map((organization: any) => ({
          id: organization.id,
          name: organization.name,
        }))}
      />
    </div>
  );
}

function OrganizationsCard({ organizations }: { organizations: any[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Organizations</CardTitle>
      </CardHeader>
      <CardContent>
        {organizations.length === 0 ? (
          <Empty message="No organization memberships." />
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {organizations.map((organization) => (
              <div key={organization.id} className="rounded-sm border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-950">{organization.name}</p>
                    <p className="font-mono text-xs text-slate-500">{organization.authOrgId}</p>
                  </div>
                  <Badge variant={organization.emailVerified ? "success" : "warning"}>
                    {organization.emailVerified ? "Verified" : "Unverified"}
                  </Badge>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
                  <MiniCount label="Members" value={organization._count.recruiters} />
                  <MiniCount label="Jobs" value={organization._count.jobPostings} />
                  <MiniCount label="Companies" value={organization._count.companies} />
                  <MiniCount label="Resumes" value={organization._count.centralResumes} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BillingCard({ billing }: { billing: any }) {
  const stripe = billing?.stripe;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Billing</CardTitle>
          <Badge variant={stripe?.available ? "success" : "warning"}>
            Stripe {stripe?.available ? "available" : "unavailable"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <MiniCount label="Subscriptions" value={billing?.subscriptions?.length || 0} />
          <MiniCount label="Invoices" value={stripe?.overview?.invoiceCount || 0} />
          <MiniCount
            label="Lifetime value"
            value={`$${Number(stripe?.overview?.lifetimeValue || 0).toFixed(2)}`}
          />
        </div>
        {billing?.subscriptions?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Stripe ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billing.subscriptions.map((subscription: any) => (
                <TableRow key={subscription.id}>
                  <TableCell>
                    {subscription.plan.displayName}{" "}
                    <span className="text-xs text-slate-500">({subscription.plan.tier})</span>
                  </TableCell>
                  <TableCell>
                    {subscription.organizationId ? "Organization" : "Individual"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{subscription.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {formatDate(subscription.currentPeriodStart)} -{" "}
                    {formatDate(subscription.currentPeriodEnd)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {subscription.stripeSubscriptionId || "Not linked"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Empty message="No theRecruit subscriptions found." />
        )}
      </CardContent>
    </Card>
  );
}

function ConfigurationCard({
  configuration,
  usageCounters,
}: {
  configuration: any;
  usageCounters: any[];
}) {
  const analyticsItems = configuration.analytics.map((record: any) => ({
    key: record.id,
    title: record.eventType,
    detail: `${record.eventCategory} · ${record.entityType || "no entity"} · ${formatDateTime(
      record.timestamp
    )}`,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuration and usage counters</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        <ConfigList
          title="Email identities"
          items={configuration.emailIdentities.map((identity: any) => ({
            key: identity.id,
            title: identity.emailAddress,
            detail: `${identity.provider} · ${
              identity.isActive ? "active" : "inactive"
            } · last used ${formatDateTime(identity.lastUsedAt)}`,
          }))}
        />
        <ConfigList
          title="Job boards"
          items={configuration.jobBoardSettings.map((board: any) => ({
            key: board.id,
            title: board.boardName || board.boardSlug,
            detail: `${board.isActive ? "active" : "inactive"} · ${board.theme} · /${
              board.boardSlug
            }`,
          }))}
        />
        <ConfigList
          title="Current usage"
          items={usageCounters.slice(0, 12).map((usage: any) => ({
            key: usage.id,
            title: usage.featureKey,
            detail: `${usage.usageCount} used${usage.limit == null ? "" : ` of ${usage.limit}`} · ${
              usage.tier
            } · ends ${formatDate(usage.periodEnd)}`,
          }))}
        />
        <ConfigList title="Recent analytics" items={analyticsItems} />
      </CardContent>
    </Card>
  );
}

function ConfigList({
  title,
  items,
}: {
  title: string;
  items: Array<{ key: string; title: string; detail: string }>;
}) {
  return (
    <div className="rounded-sm border border-slate-200 p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">None configured.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.key}>
              <p className="text-sm font-medium">{item.title}</p>
              <p className="text-xs text-slate-500">{item.detail}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResourceTable({
  resource,
  data,
  loading,
}: {
  resource: ResourceType;
  data: any;
  loading: boolean;
}) {
  if (loading && !data) return <PageMessage message="Loading related records..." />;
  if (!data?.items?.length)
    return <Empty message="No related records found for the selected filters." />;

  const renderers: Record<
    ResourceType,
    { headers: string[]; cells: (item: any) => React.ReactNode[] }
  > = {
    jobs: {
      headers: ["Job", "Status", "Context", "Candidates", "Interviews", "Created"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.title}</p>
          <p className="text-xs text-slate-500">
            {item.companyName} · {item.location || "No location"}
          </p>
        </div>,
        <Badge variant="outline">{item.status}</Badge>,
        <span className="font-mono text-xs">{shortId(item.recruiterId)}</span>,
        item._count.candidates,
        item._count.interviews,
        formatDateTime(item.createdAt),
      ],
    },
    companies: {
      headers: ["Company", "Status", "Verification", "Organization", "Jobs", "Created"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.name}</p>
          <p className="text-xs text-slate-500">
            {item.industry} · {item.website}
          </p>
        </div>,
        item.isActive ? "Active" : "Inactive",
        <Badge variant={item.emailVerified ? "success" : "warning"}>
          {item.verificationStatus}
        </Badge>,
        item.organization?.name || "Individual",
        item._count.jobPostings,
        formatDateTime(item.createdAt),
      ],
    },
    applications: {
      headers: ["Candidate", "Job", "Status", "Match", "Interviews", "Applied"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.name}</p>
          <p className="text-xs text-slate-500">
            {item.email || "No email"} · {item.phoneNumber || "No phone"}
          </p>
        </div>,
        item.jobPosting.title,
        <Badge variant="outline">{item.candidateStatus}</Badge>,
        item.matchScore == null ? "—" : `${Math.round(item.matchScore)}%`,
        item._count.interviewRequests,
        formatDateTime(item.appliedAt),
      ],
    },
    interviews: {
      headers: ["Candidate", "Job", "Status", "Score", "Responses", "Created"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.CandidateJobApplication?.name || "Unknown candidate"}</p>
          <p className="text-xs text-slate-500">
            {item.CandidateJobApplication?.email || "No email"}
          </p>
        </div>,
        item.jobPosting.title,
        <Badge variant="outline">{item.status}</Badge>,
        item.overallScore ?? "—",
        item._count.responses,
        formatDateTime(item.createdAt),
      ],
    },
    resumes: {
      headers: ["Candidate", "File", "Status", "Owner", "Applications", "Created"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.candidateName || "Unknown candidate"}</p>
          <p className="text-xs text-slate-500">{item.candidateEmail || "No email"}</p>
        </div>,
        <div>
          <p>{item.fileName}</p>
          <p className="text-xs text-slate-500">{formatBytes(item.fileSize)}</p>
        </div>,
        <Badge variant="outline">{item.processingStatus}</Badge>,
        <span className="font-mono text-xs">{item.ownerIdentifier}</span>,
        item._count.jobApplications,
        formatDateTime(item.createdAt),
      ],
    },
    usage: {
      headers: ["Feature", "Event", "Result", "Owner", "Resource", "Created"],
      cells: (item) => [
        item.featureKey,
        item.eventType,
        <Badge variant={item.success ? "success" : "warning"}>
          {item.success ? "Success" : "Failed"}
        </Badge>,
        <span className="font-mono text-xs">{item.organizationId || shortId(item.userId)}</span>,
        item.resourceType ? `${item.resourceType}: ${shortId(item.resourceId)}` : "—",
        formatDateTime(item.createdAt),
      ],
    },
    activity: {
      headers: ["Type", "Summary", "Status", "Context", "Occurred"],
      cells: (item) => [
        <Badge variant="outline">{item.kind}</Badge>,
        activitySummary(item),
        activityStatus(item),
        <span className="font-mono text-xs">
          {shortId(item.recruiterId || item.invitedById || item.orgId)}
        </span>,
        formatDateTime(item.occurredAt),
      ],
    },
  };
  const renderer = renderers[resource];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {renderer.headers.map((header) => (
            <TableHead key={header}>{header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.items.map((item: any) => (
          <TableRow key={`${item.kind || resource}-${item.id}`}>
            {renderer.cells(item).map((cell, index) => (
              <TableCell key={index}>{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm">
      <span className="text-slate-500">
        {total} records · Page {page} of {totalPages}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </span>
    </div>
  );
}
function MiniCount({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-sm bg-slate-50 p-2">
      <p className="text-lg font-semibold text-slate-950">{value}</p>
      <p className="text-slate-500">{label}</p>
    </div>
  );
}
function Empty({ message }: { message: string }) {
  return (
    <div className="rounded-sm border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}
function PageMessage({ message, error = false }: { message: string; error?: boolean }) {
  return (
    <div
      className={`rounded-sm border p-6 text-sm ${
        error ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 text-slate-500"
      }`}
    >
      {message}
    </div>
  );
}
function formatDate(value?: string | Date | null) {
  return value ? new Date(value).toLocaleDateString() : "—";
}
function formatDateTime(value?: string | Date | null) {
  return value ? new Date(value).toLocaleString() : "Never";
}
function shortId(value?: string | null) {
  return value ? (value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value) : "—";
}
function formatBytes(value?: number | null) {
  if (!value) return "Size unavailable";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}
function activitySummary(item: any) {
  if (item.kind === "invitation") return `Invitation to ${item.email}`;
  if (item.kind === "notification") return item.title;
  if (item.kind === "ai_call")
    return `${item.jobTitle || "AI call"} · ${item.companyName || "Unknown company"}`;
  return item.title || `Conversation ${shortId(item.id)}`;
}
function activityStatus(item: any) {
  if (item.status) return <Badge variant="outline">{item.status}</Badge>;
  if (item.kind === "notification") return item.isRead ? "Read" : "Unread";
  return "—";
}
